import { createSlice } from "@reduxjs/toolkit";
import { mapAndInsertProducts } from "../results/products";
import config from "../../../../../config/common";
import {
  extendOffsetLowerBound,
  extendOffsetUpperBound,
  getOffsetFromQuery,
} from "../pagination/helpers/offset";
import getPreviousPageNumber from "../pagination/selectors/getPreviousPageNumber";
import getNextPageNumber from "../pagination/selectors/getNextPageNumber";
import { registerResultsSlice } from "../results";
import { registerQuerySlice } from "../query";
import { registerDiagnosticsSlice } from "../diagnostics/slice";
import {
  getReceiveSearchDiagnostic,
  mergeDiagnostics,
} from "../diagnostics/helpers";
import { registerUiSlice } from "../ui";
import { searchProductsAdapter } from "../../productEntityAdapter";
import { dismissFloorFilter } from "./actionCreators";

const pageSize = config.api.product.search.pageSize;

const defaultPageSize = 72;

export const initialState = {
  searchTerm: "",
  redirectUrl: "",
  products: [],
  productsEntity: searchProductsAdapter.getInitialState(),
  facets: [],
  error: null,
  isLoading: false,
  isNewSearch: true,
  itemCount: 0,
  offset: {
    initial: 0,
    lower: 0,
    upper: pageSize,
  },
  isLoadingMore: false,
  isLoadingPrevious: false,
  ui: {
    isInitialLoading: false,
    isPagingForward: false,
    isPagingBackward: false,
    lastError: null,
  },
  showLoadMore: false,
  tileToFocus: {
    first: false,
    last: false,
    pageNumber: null,
  },
  diagnostics: {
    recommendationsAnalytics: {},
  },
  searchPassMeta: {
    alternateSearchTerms: [],
  },
  personalisationParams: {},
  pageSize: defaultPageSize,
  dismissedFloors: {},
};

const shouldShowLoadMore = (offset, itemCount) => offset.upper < itemCount;

const shouldShowLoadPrevious = (offset) => offset.lower > 0;

const hasFilterReturnedNoResults = (newItemCount, previousState) =>
  newItemCount === 0 && previousState.facets.length > 0;

const getDismissedFloorsState = (dismissedFloors, floor) => ({
  ...dismissedFloors,
  [floor]: true,
});

const registerSearchFilterStateSlice = (builder) => {
  builder.addCase(dismissFloorFilter, (state, { payload: floor }) => {
    state.dismissedFloors = getDismissedFloorsState(
      state.dismissedFloors,
      floor,
    );
  });
};

const syncProductsEntity = (state) => {
  if (!state.productsEntity) {
    state.productsEntity = searchProductsAdapter.getInitialState();
  }

  searchProductsAdapter.setAll(state.productsEntity, state.products || []);
};

const searchSlice = createSlice({
  name: "search",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    registerUiSlice(builder);
    registerResultsSlice(builder, {
      initialState,
      extendOffsetLowerBound,
      extendOffsetUpperBound,
      mapAndInsertProducts,
      getPreviousPageNumber,
      getNextPageNumber,
      mergeDiagnostics,
      shouldShowLoadMore,
      shouldShowLoadPrevious,
      getOffsetFromQuery,
      getReceiveSearchDiagnostic,
      hasFilterReturnedNoResults,
      pageSize,
      syncProductsEntity,
    });
    registerSearchFilterStateSlice(builder);
    registerQuerySlice(builder);
    registerDiagnosticsSlice(builder, { syncProductsEntity });
  },
});

export default searchSlice.reducer;
